import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserContext } from '@/lib/auth/useUserContext';
import { hasAnyGroup } from '@/lib/auth/rbac';

interface Props {
  /** Group names the user must have at least one of (e.g. ['TMKN-Qargo-Admins']). */
  groups: string[];
  children: ReactNode;
  /** Where to send users who lack any of the required groups. */
  fallback?: string;
}

/**
 * Gate a route on group membership. Must be nested inside <RequireAuth> so that
 * `groups` is populated. Shows a brief loading state while /me resolves; if the
 * authenticated user is not in any of the listed groups, redirects to `fallback`.
 */
export function RequireAnyGroup({ groups, children, fallback = '/dashboard' }: Props): ReactNode {
  const { groups: userGroups, loading, isAuthenticated } = useUserContext();

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }
  if (loading && userGroups.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Checking permissions…
      </div>
    );
  }
  if (!hasAnyGroup(userGroups, groups)) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}
